/**
 * Página de éxito de BioTemplatePremium (USD/Stripe).
 *
 * Al montar, valida el pago contra la API de Stripe via:
 * - GET /api/validate-payment?payment_intent=pi_xxx
 * - GET /api/validate-payment?session_id=cs_xxx (checkout de suscripción)
 *
 * Solo muestra el CTA si el pago fue realmente aprobado.
 * Standard → Telegram. VIP → portal exclusivo (registro).
 */

import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { AlertCircle, CheckCircle, Crown, ExternalLink, Loader2, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

/**
 * Redirect URLs are now configured per brand via /dashboard/subscripciones/planes.
 * These fallbacks are used only when the API returns null (not yet configured).
 * For a new brand: set them via the dashboard → Planes de Suscripción → URL de éxito.
 */
const FALLBACK_TELEGRAM_URL = import.meta.env.VITE_SUCCESS_REDIRECT_STANDARD as string | undefined;
const FALLBACK_VIP_URL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP as string | undefined;

// ─── Labels por idioma ────────────────────────────────────────────────────────

interface SuccessLabels {
  // Plan basic — acceso directo al grupo privado de Telegram
  basicTitle: string;
  basicSubtitle: string;
  basicMessage: string;
  basicCta: string;
  // Plan standard — canal Telegram estándar
  title: string;
  subtitle: string;
  processedBy: string;
  standardMessage: string;
  standardCta: string;
  // Plan VIP — portal exclusivo
  vipThanksTitle: string;
  vipThanksSubtitle: string;
  vipMessage: string;
  vipCta: string;
  vipFooterHint: string;
  // Estados
  validating: string;
  errorTitle: string;
  errorHelp: string;
}

const LABELS: Record<string, SuccessLabels> = {
  es: {
    basicTitle: "¡Acceso activado!",
    basicSubtitle: "Tu membresía Basic está lista",
    basicMessage: "Ya podés ingresar al grupo privado de Telegram. Hacé clic en el botón para unirte ahora.",
    basicCta: "Ingresar al Grupo Privado",
    title: "¡Pago exitoso!",
    subtitle: "Tu acceso está listo",
    processedBy: "Procesado por",
    standardMessage: `Bienvenido al canal exclusivo de ${BRAND_PERSON_NAME}. Hacé clic para unirte al grupo de Telegram.`,
    standardCta: "Ingresar al canal",
    vipThanksTitle: "¡Muchas gracias!",
    vipThanksSubtitle: "Tu membresía VIP ya está activa.",
    vipMessage:
      "Para continuar y acceder a todo el contenido exclusivo, ingresá al portal exclusivo y completá tu registro.",
    vipCta: "Ir al portal exclusivo",
    vipFooterHint: "Se abrirá el registro del portal en una nueva pestaña.",
    validating: "Verificando tu pago...",
    errorTitle: "No pudimos verificar tu pago",
    errorHelp: "Si realizaste el pago correctamente, contactá al soporte con tu número de transacción.",
  },
  en: {
    basicTitle: "Access activated!",
    basicSubtitle: "Your Basic membership is ready",
    basicMessage: "You can now join the private Telegram group. Click the button below to enter.",
    basicCta: "Join Private Group",
    title: "Payment successful!",
    subtitle: "Your access is ready",
    processedBy: "Processed by",
    standardMessage: `Welcome to ${BRAND_PERSON_NAME}'s exclusive channel. Click to join the Telegram group.`,
    standardCta: "Join the channel",
    vipThanksTitle: "Thank you so much!",
    vipThanksSubtitle: "Your VIP membership is now active.",
    vipMessage:
      "To continue and access all exclusive content, go to the exclusive portal and complete your registration.",
    vipCta: "Go to exclusive portal",
    vipFooterHint: "Registration will open in a new tab.",
    validating: "Verifying your payment...",
    errorTitle: "We could not verify your payment",
    errorHelp: "If you completed the payment, please contact support with your transaction number.",
  },
  pt: {
    basicTitle: "Acesso ativado!",
    basicSubtitle: "Sua assinatura Basic está pronta",
    basicMessage: "Agora você pode entrar no grupo privado do Telegram. Clique no botão abaixo para ingressar.",
    basicCta: "Entrar no Grupo Privado",
    title: "Pagamento aprovado!",
    subtitle: "Seu acesso está pronto",
    processedBy: "Processado por",
    standardMessage: `Bem-vindo ao canal exclusivo da ${BRAND_PERSON_NAME}. Clique para entrar no grupo do Telegram.`,
    standardCta: "Entrar no canal",
    vipThanksTitle: "Muito obrigado!",
    vipThanksSubtitle: "Sua assinatura VIP já está ativa.",
    vipMessage:
      "Para continuar e acessar todo o conteúdo exclusivo, entre no portal exclusivo e complete seu cadastro.",
    vipCta: "Ir ao portal exclusivo",
    vipFooterHint: "O cadastro abrirá em uma nova aba.",
    validating: "Verificando seu pagamento...",
    errorTitle: "Não conseguimos verificar seu pagamento",
    errorHelp: "Se você concluiu o pagamento, entre em contato com o suporte com seu número de transação.",
  },
  de: {
    basicTitle: "Zugang aktiviert!",
    basicSubtitle: "Deine Basic-Mitgliedschaft ist bereit",
    basicMessage: "Du kannst jetzt der privaten Telegram-Gruppe beitreten. Klick auf den Button, um beizutreten.",
    basicCta: "Private Gruppe beitreten",
    title: "Zahlung erfolgreich!",
    subtitle: "Dein Zugang ist bereit",
    processedBy: "Verarbeitet von",
    standardMessage: `Willkommen im exklusiven Kanal von ${BRAND_PERSON_NAME}. Klick, um der Telegram-Gruppe beizutreten.`,
    standardCta: "Kanal beitreten",
    vipThanksTitle: "Vielen Dank!",
    vipThanksSubtitle: "Deine VIP-Mitgliedschaft ist jetzt aktiv.",
    vipMessage:
      "Um fortzufahren und alle exklusiven Inhalte zu sehen, gehe ins exklusive Portal und schließe deine Registrierung ab.",
    vipCta: "Zum exklusiven Portal",
    vipFooterHint: "Die Registrierung öffnet sich in einem neuen Tab.",
    validating: "Zahlung wird überprüft...",
    errorTitle: "Zahlung konnte nicht verifiziert werden",
    errorHelp: "Wenn du die Zahlung abgeschlossen hast, kontaktiere den Support mit deiner Transaktionsnummer.",
  },
  fr: {
    basicTitle: "Accès activé !",
    basicSubtitle: "Votre abonnement Basic est prêt",
    basicMessage: "Vous pouvez maintenant rejoindre le groupe privé Telegram. Cliquez sur le bouton pour y accéder.",
    basicCta: "Rejoindre le Groupe Privé",
    title: "Paiement réussi !",
    subtitle: "Votre accès est prêt",
    processedBy: "Traité par",
    standardMessage: `Bienvenue sur la chaîne exclusive de ${BRAND_PERSON_NAME}. Cliquez pour rejoindre le groupe Telegram.`,
    standardCta: "Rejoindre la chaîne",
    vipThanksTitle: "Merci beaucoup !",
    vipThanksSubtitle: "Votre abonnement VIP est maintenant actif.",
    vipMessage:
      "Pour continuer et accéder à tout le contenu exclusif, rendez-vous sur le portail exclusif et finalisez votre inscription.",
    vipCta: "Aller au portail exclusif",
    vipFooterHint: "L'inscription s'ouvrira dans un nouvel onglet.",
    validating: "Vérification du paiement...",
    errorTitle: "Nous n'avons pas pu vérifier votre paiement",
    errorHelp: "Si vous avez effectué le paiement, contactez le support avec votre numéro de transaction.",
  },
  it: {
    basicTitle: "Accesso attivato!",
    basicSubtitle: "Il tuo abbonamento Basic è pronto",
    basicMessage: "Ora puoi entrare nel gruppo privato Telegram. Clicca sul pulsante per accedere.",
    basicCta: "Entra nel Gruppo Privato",
    title: "Pagamento riuscito!",
    subtitle: "Il tuo accesso è pronto",
    processedBy: "Elaborato da",
    standardMessage: `Benvenuto nel canale esclusivo di ${BRAND_PERSON_NAME}. Clicca per unirti al gruppo Telegram.`,
    standardCta: "Entra nel canale",
    vipThanksTitle: "Grazie mille!",
    vipThanksSubtitle: "Il tuo abbonamento VIP è ora attivo.",
    vipMessage:
      "Per continuare e accedere a tutti i contenuti esclusivi, entra nel portale esclusivo e completa la registrazione.",
    vipCta: "Vai al portale esclusivo",
    vipFooterHint: "La registrazione si aprirà in una nuova scheda.",
    validating: "Verifica del pagamento in corso...",
    errorTitle: "Non siamo riusciti a verificare il tuo pagamento",
    errorHelp: "Se hai completato il pagamento, contatta il supporto con il tuo numero di transazione.",
  },
};

function getLabels(language: string): SuccessLabels {
  const base = language.split("-")[0].toLowerCase();
  return LABELS[base] ?? LABELS["en"];
}

type ValidationState = "loading" | "valid" | "error";

// ─── Componente ───────────────────────────────────────────────────────────────

export default function PremiumInviteSuccess() {
  const [searchParams] = useSearchParams();
  const { language: workerLanguage } = useTemplateSelection();
  const capturePremiumEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const posthog = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture(event, {
        page_path: window.location.pathname,
        page_url: window.location.href,
        template_type: "PremiumInviteSuccess",
        landing_surface: window.location.pathname.includes("/invitacion-premium")
          ? "invitacion_premium_success"
          : "premium_success_other",
        ...props,
      });
    }
  };

  const language        = searchParams.get("lang") || workerLanguage || "es";
  const provider        = searchParams.get("provider") ?? "stripe";
  const gatewayLabel    = provider === "mercadopago" ? "Mercado Pago" : "Stripe";
  const paymentIntentId = searchParams.get("payment_intent");
  const sessionId       = searchParams.get("session_id") || searchParams.get("checkout_session_id");
  const planFromUrl     = searchParams.get("plan");
  const flowTraceId     = searchParams.get("flow_trace_id") || undefined;
  /** URL del grupo de Telegram pasada por BioTemplatePremiumSimple al iniciar el checkout. */
  const telegramUrlFromParam = searchParams.get("telegram_url") || undefined;

  const L = getLabels(language);

  const [validationState, setValidationState] = useState<ValidationState>("loading");
  const [validatedPlan, setValidatedPlan]     = useState<"basic" | "standard" | "vip">("basic");
  const [redirectUrl, setRedirectUrl]         = useState<string | null>(null);
  const [redirectLabel, setRedirectLabel]     = useState<string | null>(null);
  const [errorMessage, setErrorMessage]       = useState<string | null>(null);

  useEffect(() => {
    let effectiveSessionId: string | null = sessionId;
    if (!paymentIntentId && !effectiveSessionId) {
      try {
        const raw = localStorage.getItem("premium_last_stripe_session");
        if (raw) {
          const parsed = JSON.parse(raw) as {
            sessionId?: string;
            flowTraceId?: string;
            createdAt?: number;
          };
          const candidate = typeof parsed.sessionId === "string" ? parsed.sessionId.trim() : "";
          const ageMs =
            typeof parsed.createdAt === "number" ? Date.now() - parsed.createdAt : Number.POSITIVE_INFINITY;
          const traceMatches =
            !flowTraceId ||
            !parsed.flowTraceId ||
            String(parsed.flowTraceId).trim() === String(flowTraceId).trim();
          const freshEnough = ageMs < 2 * 60 * 60 * 1000;
          if (candidate && freshEnough && traceMatches) {
            effectiveSessionId = candidate;
          }
        }
      } catch {
        // ignore fallback parse errors
      }
    }

    if (!paymentIntentId && !effectiveSessionId) {
      // Sin identificador de pago en URL → no podemos validar → error
      capturePremiumEvent("premium_success_missing_identifier", {
        provider,
        language,
        flow_trace_id: flowTraceId,
        has_payment_intent: false,
        has_session_id: false,
      });
      setErrorMessage("No se encontró el identificador del pago en la URL (payment_intent/session_id).");
      setValidationState("error");
      return;
    }

    const validate = async () => {
      try {
        capturePremiumEvent("premium_success_validation_started", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          has_payment_intent: Boolean(paymentIntentId),
          has_session_id: Boolean(effectiveSessionId),
        });
        const traceParam = flowTraceId ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}` : "";
        const url = paymentIntentId
          ? `/api/validate-payment?payment_intent=${encodeURIComponent(paymentIntentId)}${traceParam}`
          : `/api/validate-payment?session_id=${encodeURIComponent(effectiveSessionId as string)}${traceParam}`;
        const res = await fetch(url);
        const data = await res.json() as { valid: boolean; plan?: string; error?: string };

        if (!res.ok || !data.valid) {
          capturePremiumEvent("premium_success_validation_failed", {
            provider,
            language,
            flow_trace_id: flowTraceId,
            status: res.status,
            error: data.error || "invalid_payment",
            payment_intent: paymentIntentId || undefined,
            session_id: effectiveSessionId || undefined,
          });
          setErrorMessage(data.error || "El pago no pudo ser verificado.");
          setValidationState("error");
          return;
        }

        // "basic" del URL param tiene prioridad porque validate-payment puede retornar
        // "standard" como fallback cuando el tier no se reconoce en la DB.
        // La API ahora sí devuelve "basic" si está en los metadata de Stripe, pero
        // el URL param es la fuente más confiable para este flow.
        const resolvedPlan = planFromUrl === "basic"
          ? "basic"
          : (data.plan || planFromUrl || "standard");
        capturePremiumEvent("premium_success_validated", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          plan_tier: resolvedPlan,
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        setValidatedPlan(
          resolvedPlan === "vip"
            ? "vip"
            : resolvedPlan === "standard"
              ? "standard"
              : "basic"
        );
        setValidationState("valid");
      } catch {
        capturePremiumEvent("premium_success_validation_failed", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          error: "network_error",
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        setErrorMessage("Error de conexión al verificar el pago.");
        setValidationState("error");
      }
    };

    validate();
  }, [paymentIntentId, sessionId, planFromUrl, flowTraceId, provider, language]);

  // ── Fetch redirect URL for the validated plan tier ────────────────────────
  // Called once the payment is validated and we know the plan tier.
  useEffect(() => {
    if (validationState !== "valid") return;
    const fetchRedirectUrl = async () => {
      try {
        const res = await fetch(`/api/membership/success-config?plan_tier=${encodeURIComponent(validatedPlan)}`);
        if (!res.ok) return;
        const data = await res.json() as {
          success_redirect_url?: string | null;
          success_redirect_label?: string | null;
        };
        if (data.success_redirect_url) setRedirectUrl(data.success_redirect_url);
        if (data.success_redirect_label) setRedirectLabel(data.success_redirect_label);
      } catch {
        // Non-fatal — falls back to env vars / hardcoded defaults below
      }
    };
    void fetchRedirectUrl();
  }, [validationState, validatedPlan]);

  // ── Derivados visuales ────────────────────────────────────────────────────
  const isVip      = validatedPlan === "vip";
  const isBasic    = validatedPlan === "basic";
  // basic → sky/Telegram blue  |  standard → green  |  vip → amber/gold
  const gradient = isVip
    ? "from-amber-400 via-orange-500 to-yellow-500"
    : isBasic
      ? "from-sky-400 via-blue-500 to-cyan-500"
      : "from-green-500 via-emerald-500 to-teal-500";
  const glowColor = isVip
    ? "bg-amber-500/40"
    : isBasic
      ? "bg-sky-500/40"
      : "bg-green-500/40";
  const btnGradient = isVip
    ? "from-amber-500 to-orange-500"
    : isBasic
      ? "from-sky-500 to-blue-600"
      : "from-green-500 to-emerald-500";

  // Resolve the redirect URL — priority:
  // 1. DB-configured URL (via dashboard, from /api/membership/success-config)
  // 2. URL passed as query param (telegram_url) — for flows that embed the link
  // 3. Env var fallback (VITE_SUCCESS_REDIRECT_STANDARD / VITE_SUCCESS_REDIRECT_VIP)
  // 4. null → CTA button hidden (not yet configured for this brand)
  const primaryCtaUrl = redirectUrl
    ?? (isVip ? null : (telegramUrlFromParam ?? FALLBACK_TELEGRAM_URL ?? null))
    ?? (isVip ? FALLBACK_VIP_URL ?? null : null);

  const pageTitle    = isVip ? L.vipThanksTitle    : isBasic ? L.basicTitle    : L.title;
  const pageSubtitle = isVip ? L.vipThanksSubtitle : isBasic ? L.basicSubtitle : L.subtitle;
  const message      = isVip ? L.vipMessage        : isBasic ? L.basicMessage  : L.standardMessage;
  // Use dashboard-configured label if available, else fall back to i18n labels
  const ctaLabel     = redirectLabel ?? (isVip ? L.vipCta : isBasic ? L.basicCta : L.standardCta);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (validationState === "loading") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black" />
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">{L.validating}</p>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (validationState === "error") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-pink-500 to-rose-500 opacity-15" />
        </div>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="relative inline-flex justify-center w-full">
            <div className="absolute inset-0 blur-2xl bg-red-500/30 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center shadow-2xl">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-red-400">{L.errorTitle}</h1>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-red-500/20">
            <p className="text-gray-300 text-sm leading-relaxed mb-2">{errorMessage}</p>
            <p className="text-gray-500 text-xs">{L.errorHelp}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Éxito ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-black text-white flex items-center justify-center px-4 select-none"
      style={{ fontFamily: FONT_FAMILY, WebkitUserSelect: "none" } as React.CSSProperties}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      <div className="fixed inset-0 -z-10 bg-black">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative inline-flex justify-center w-full">
          <div className={`absolute inset-0 blur-2xl ${glowColor} animate-pulse`} />
          <div className={`relative w-24 h-24 bg-gradient-to-br ${gradient} rounded-full flex items-center justify-center shadow-2xl`}>
            {isVip
              ? <Crown className="w-12 h-12 text-white" />
              : <CheckCircle className="w-12 h-12 text-white" />
            }
          </div>
        </div>

        <div>
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
            {pageTitle}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {pageSubtitle}
          </p>
        </div>

        <div
          className={`rounded-2xl p-6 border backdrop-blur-sm select-none ${
            isVip
              ? "border-amber-500/30 bg-amber-500/5"
              : isBasic
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-white/10 bg-white/5"
          }`}
          onContextMenu={(e) => e.preventDefault()}
        >
          <p className="text-gray-300 text-base leading-relaxed mb-4">{message}</p>

          {/* "Procesado por" solo para VIP y standard — basic es más directo */}
          {!isBasic && (
            <p className="text-gray-500 text-xs mb-6">
              {L.processedBy} {gatewayLabel}
            </p>
          )}

          {primaryCtaUrl ? (
            <a
              href={primaryCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center gap-2 select-none`}
              onContextMenu={(e) => e.preventDefault()}
              onClick={() =>
                capturePremiumEvent(
                  isVip
                    ? "premium_success_vip_portal_click"
                    : isBasic
                      ? "premium_success_basic_telegram_click"
                      : "premium_success_telegram_click",
                  {
                    provider,
                    language,
                    flow_trace_id: flowTraceId,
                    plan_tier: validatedPlan,
                    destination_url: primaryCtaUrl,
                    payment_intent: paymentIntentId || undefined,
                    session_id: sessionId || undefined,
                  },
                )
              }
            >
              {isVip ? (
                <ExternalLink className="w-5 h-5" />
              ) : (
                /* Telegram icon for basic and standard plans */
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              )}
              <span>{ctaLabel}</span>
            </a>
          ) : (
            /* No redirect URL configured yet for this brand */
            <div className="w-full py-4 px-6 rounded-xl border border-white/10 text-gray-500 text-sm text-center">
              Tu membresía está activa. El acceso será habilitado en breve — te contactaremos por email.
            </div>
          )}
          {isVip && primaryCtaUrl && (
            <p className="text-gray-500 text-xs mt-4 text-center">{L.vipFooterHint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
